import {
  authenticatedFetch,
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
} from "../../../api/authenticatedFetch.js";

const BASE = "/api/v1/board-reports";

/** GET /api/v1/board-reports -> { board_reports: [...] } */
export const listBoardReports = async () => {
  const data = await authenticatedGet(BASE);
  return data.board_reports || [];
};

/**
 * GET /api/v1/board-report-templates -> { templates: [...] }
 *
 * Note the endpoint is a sibling path, not under BASE - the template catalog
 * is not scoped to a specific board report. Each item is
 * { type, name, description, pages, available }; consumed by the create
 * form's template picker (BoardReportConfig).
 */
export const listBoardReportTemplates = async () => {
  const data = await authenticatedGet("/api/v1/board-report-templates");
  return data.templates || [];
};

/** POST /api/v1/board-reports -> { board_report: {...} } */
export const createBoardReport = async (body = {}) => {
  const data = await authenticatedPost(BASE, body);
  return data.board_report;
};

/** GET /api/v1/board-reports/{id} -> { board_report: {...} } */
export const getBoardReport = async (id) => {
  const data = await authenticatedGet(`${BASE}/${id}`);
  return data.board_report;
};

/** PUT /api/v1/board-reports/{id} -> { board_report: {...} } */
export const updateBoardReport = async (id, body = {}) => {
  const data = await authenticatedPut(`${BASE}/${id}`, body);
  return data.board_report;
};

/** DELETE /api/v1/board-reports/{id} -> 204 */
export const deleteBoardReport = async (id) => {
  await authenticatedDelete(`${BASE}/${id}`);
};

/**
 * GET /api/v1/board-reports/{id}/manifest -> { manifest: {...} }
 *
 * Returns the RESOLVED template manifest (seed structure + this report's
 * manifest_override already applied): fixed page/block structure, with each
 * block's title/binding/optional reflecting any admin edits already made via
 * the template editor. There is no unresolved/seed-only variant exposed to
 * the frontend.
 */
export const getBoardReportManifest = async (id) => {
  const data = await authenticatedGet(`${BASE}/${id}/manifest`);
  return data.manifest;
};

/**
 * GET /api/v1/board-reports/{id}/runs/{runId} -> { run: {...}, manifest: {...} }
 *
 * The route needs both ids: a run only exists nested under its board report,
 * and the resolved manifest depends on that board report's manifest_override.
 * Returns the bare `{ run, manifest }` object (no outer envelope key) - see
 * GetBoardReportRunDetail on the backend.
 */
export const getBoardReportRun = async (id, runId) => {
  return authenticatedGet(`${BASE}/${id}/runs/${runId}`);
};

/**
 * GET /api/v1/board-reports/{id}/runs -> { runs: [...] }
 *
 * Lists every run (draft through archived) for a board report, newest version
 * first (ListBoardReportRuns orders by version DESC). Each run is an
 * immutable snapshot - this is the full version history behind
 * BoardReportRunHistory (/board-reports/:id/runs).
 */
export const listBoardReportRuns = async (id) => {
  const data = await authenticatedGet(`${BASE}/${id}/runs`);
  return data.runs || [];
};

/**
 * POST /api/v1/board-reports/{id}/runs -> { run: {...} }
 *
 * Creates a new draft run for the report's configured period: computes the
 * deterministic narrative and drafts it via the LLM seam (falls back to the
 * deterministic candidate with narrative.provisional=true on LLM failure).
 */
export const createRun = async (id) => {
  const data = await authenticatedPost(`${BASE}/${id}/runs`);
  return data.run;
};

/**
 * PUT /api/v1/board-reports/{id}/runs/{runId}/narrative -> { run: {...} }
 *
 * Replaces the run's editable narrative (snake_case keys: status, bullets,
 * what_changed, why, what_to_do, provisional). Allowed only while the run is
 * draft or in_review - editing a draft flips it to in_review as a side
 * effect; any other status (approved/sent/archived) returns 409.
 */
export const updateRunNarrative = async (id, runId, narrative) => {
  const data = await authenticatedPut(`${BASE}/${id}/runs/${runId}/narrative`, narrative);
  return data.run;
};

/**
 * PUT /api/v1/board-reports/{id}/runs/{runId}/drivers -> { run: {...} }
 *
 * Replaces the run's driver annotations wholesale (the JSON body is the bare
 * array of {driver, impact, cause, expected, owner, action} objects, not
 * wrapped in an envelope key). Driver/impact are seeded from the MoM
 * waterfall at run creation and treated as read-only client-side; cause/
 * expected/owner/action are author-entered. Allowed only while the run is
 * draft or in_review (same narrativeEditAllowed gate as the narrative PUT) -
 * any other status returns 409. Unlike the narrative PUT, this does NOT flip
 * draft -> in_review as a side effect (backend decision - drivers are a
 * second, independently-editable annotation surface on the same run).
 */
export const updateRunDrivers = async (id, runId, drivers) => {
  const data = await authenticatedPut(`${BASE}/${id}/runs/${runId}/drivers`, drivers);
  return data.run;
};

/**
 * POST /api/v1/board-reports/{id}/runs/{runId}/approve -> { run: {...} }
 *
 * Marks the run approved and stamps the approving user. Allowed only from
 * draft or in_review - any other source status returns 409 (a one-way
 * freeze, mirrored client-side by disabling narrative edits once approved).
 */
export const approveRun = async (id, runId) => {
  const data = await authenticatedPost(`${BASE}/${id}/runs/${runId}/approve`);
  return data.run;
};

/**
 * GET /api/v1/board-reports/{id}/runs/{runId}/download -> the FROZEN PDF
 * artifact bytes for that run (Content-Type: application/pdf,
 * Content-Disposition: attachment). 404s when the run has no artifact_id or
 * its retention window has expired.
 *
 * Uses the base authenticatedFetch (not authenticatedGet, which parses the
 * response as JSON) and reads the body as a Blob - this is binary content,
 * not an API envelope. Returns the blob plus a filename parsed from
 * Content-Disposition, falling back to a generated name when that header is
 * missing or unparseable.
 */
export const downloadRunArtifact = async (id, runId) => {
  const response = await authenticatedFetch(`${BASE}/${id}/runs/${runId}/download`, {
    method: "GET",
  });
  const blob = await response.blob();

  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match ? match[1] : `board-report-${id}-run-${runId}.pdf`;

  return { blob, filename };
};
